import React, { useState, useEffect } from 'react';
import merchantKycService from '../services/merchantKycService';
import SimpleDocumentUpload from './SimpleDocumentUpload';
import './MerchantKycDocuments.css';

const MerchantKycDocuments = ({ merchantProfile, onProfileUpdate }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDocuments();
  }, [merchantProfile]);

  const loadDocuments = () => {
    try {
      const merchantDocuments = merchantKycService.getMerchantDocuments(merchantProfile.merchantId);
      const docs = merchantDocuments.documents || [];
      
      // Ensure each document has required properties
      const validatedDocs = docs.map(doc => ({
        id: doc.id || Date.now() + Math.random(),
        type: doc.type || 'OTHER',
        fileName: doc.fileName || 'Unknown File',
        fileSize: doc.fileSize || 0,
        uploadedAt: doc.uploadedAt || new Date().toISOString(),
        status: doc.status || 'uploaded',
        verificationResult: doc.verificationResult || null
      }));
      
      setDocuments(validatedDocs);
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUploaded = (uploadedDoc) => {
    // Update documents in service
    const updatedDocuments = [...documents, uploadedDoc];
    merchantKycService.saveMerchantDocuments(merchantProfile.merchantId, updatedDocuments);
    setDocuments(updatedDocuments);

    // Update profile status
    const updatedProfile = merchantKycService.updateMerchantKycStatus(
      merchantProfile.merchantId,
      { 
        status: 'IN_PROGRESS',
        lastUpdated: new Date().toISOString()
      }
    );

    if (updatedProfile && onProfileUpdate) {
      onProfileUpdate({
        ...merchantProfile,
        kycStatus: updatedProfile
      });
    }

    if (window.showNotification) {
      window.showNotification('Document uploaded successfully!', 'success');
    }
  };

  const handleVerificationComplete = (results) => {
    // Save verification results
    merchantKycService.saveMerchantVerification(merchantProfile.merchantId, results);
    
    // Update documents with verification results
    const updatedDocuments = results.documents || documents;
    merchantKycService.saveMerchantDocuments(merchantProfile.merchantId, updatedDocuments);
    setDocuments(updatedDocuments);

    // Update profile
    const updatedProfile = merchantKycService.updateMerchantKycStatus(
      merchantProfile.merchantId,
      { 
        status: results.status || 'PENDING_REVIEW',
        completionPercentage: results.finalScore || 85,
        submittedAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      }
    );

    if (updatedProfile && onProfileUpdate) {
      onProfileUpdate({
        ...merchantProfile,
        kycStatus: updatedProfile
      });
    }

    if (window.showNotification) {
      window.showNotification('Document verification completed!', 'success');
    }
  };

  const deleteDocument = (docId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      const updatedDocuments = documents.filter(doc => doc.id !== docId);
      merchantKycService.saveMerchantDocuments(merchantProfile.merchantId, updatedDocuments);
      setDocuments(updatedDocuments);

      if (window.showNotification) {
        window.showNotification('Document deleted successfully', 'info');
      }
    }
  };

  const getDocumentIcon = (type) => {
    const icons = {
      'PAN_CARD': '🆔',
      'AADHAAR_CARD': '📋',
      'BUSINESS_REGISTRATION': '📄',
      'BANK_STATEMENT': '🏦',
      'GST_CERTIFICATE': '📜',
      'SELFIE': '🤳',
      'OTHER': '📎'
    };
    return icons[type] || '📎';
  };

  const getDocumentStatus = (doc) => {
    if (doc.verificationResult) {
      const score = doc.verificationResult.score || 0;
      if (score >= 75) return { status: 'verified', color: '#28a745', text: 'Verified' };  // Lowered from 85 to 75
      if (score >= 60) return { status: 'review', color: '#ffc107', text: 'Under Review' };  // Lowered from 70 to 60
      return { status: 'rejected', color: '#dc3545', text: 'Needs Attention' };
    }
    return { status: 'uploaded', color: '#6c757d', text: 'Uploaded' };
  };

  if (loading) {
    return (
      <div className="merchant-kyc-documents">
        <div className="loading-spinner">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="merchant-kyc-documents">
      {/* Document Upload Section */}
      <div className="upload-section">
        <h3>📄 Document Upload</h3>
        <p>Upload the required documents for KYC verification. All documents should be clear and readable.</p>
        
        <SimpleDocumentUpload
          merchantId={merchantProfile.merchantId}
          onDocumentUploaded={handleDocumentUploaded}
          onVerificationComplete={handleVerificationComplete}
        />
      </div>

      {/* Uploaded Documents */}
      {documents.length > 0 && (
        <div className="documents-list">
          <h3>📋 Uploaded Documents ({documents.length})</h3>
          <div className="documents-grid">
            {documents.map((doc, index) => {
              try {
                const status = getDocumentStatus(doc);
                return (
                  <div key={doc.id || index} className="document-card">
                    <div className="document-header">
                      <div className="document-icon">
                        {getDocumentIcon(doc.type)}
                      </div>
                      <div className="document-info">
                        <h4>{(doc.type || 'Unknown Document').replace(/_/g, ' ')}</h4>
                        <p>{doc.fileName || 'Unknown File'}</p>
                      </div>
                      <div className="document-actions">
                        <button
                          className="btn-delete"
                          onClick={() => deleteDocument(doc.id)}
                          title="Delete document"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    
                    <div className="document-details">
                      <div className="detail-item">
                        <span>Size:</span>
                        <span>{doc.fileSize ? `${(doc.fileSize / 1024).toFixed(1)} KB` : 'Unknown'}</span>
                      </div>
                      <div className="detail-item">
                        <span>Uploaded:</span>
                        <span>{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : 'Unknown'}</span>
                      </div>
                      <div className="detail-item">
                        <span>Status:</span>
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: status.color }}
                        >
                          {status.text}
                        </span>
                      </div>
                    </div>

                    {doc.verificationResult && (
                      <div className="verification-result">
                        <h5>Verification Result</h5>
                        <div className="result-score">
                          Score: {doc.verificationResult.score || 0}%
                        </div>
                        {doc.verificationResult.issues && doc.verificationResult.issues.length > 0 && (
                          <div className="result-issues">
                            <strong>Issues:</strong>
                            <ul>
                              {doc.verificationResult.issues.map((issue, idx) => (
                                <li key={idx}>{issue}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              } catch (error) {
                console.error('Error rendering document:', error, doc);
                return (
                  <div key={index} className="document-card error-card">
                    <div className="document-header">
                      <div className="document-icon">❌</div>
                      <div className="document-info">
                        <h4>Error Loading Document</h4>
                        <p>Unable to display document data</p>
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        </div>
      )}

      {/* Required Documents Guide */}
      <div className="documents-guide">
        <h3>📋 Required Documents</h3>
        <div className="guide-grid">
          <div className="guide-item">
            <div className="guide-icon">🆔</div>
            <div className="guide-content">
              <h4>PAN Card</h4>
              <p>Clear photo of your PAN card showing all details</p>
              <span className="required">Required</span>
            </div>
          </div>
          
          <div className="guide-item">
            <div className="guide-icon">📋</div>
            <div className="guide-content">
              <h4>Aadhaar Card</h4>
              <p>Front and back of Aadhaar card or Aadhaar letter</p>
              <span className="required">Required</span>
            </div>
          </div>
          
          <div className="guide-item">
            <div className="guide-icon">📄</div>
            <div className="guide-content">
              <h4>Business Registration</h4>
              <p>Business registration certificate or incorporation document</p>
              <span className="required">Required</span>
            </div>
          </div>
          
          <div className="guide-item">
            <div className="guide-icon">🏦</div>
            <div className="guide-content">
              <h4>Bank Statement</h4>
              <p>Recent bank statement (last 3 months)</p>
              <span className="optional">Optional</span>
            </div>
          </div>
          
          <div className="guide-item">
            <div className="guide-icon">📜</div>
            <div className="guide-content">
              <h4>GST Certificate</h4>
              <p>GST registration certificate if applicable</p>
              <span className="optional">Optional</span>
            </div>
          </div>
          
          <div className="guide-item">
            <div className="guide-icon">🤳</div>
            <div className="guide-content">
              <h4>Selfie</h4>
              <p>Clear selfie for identity verification</p>
              <span className="required">Required</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="upload-tips">
        <h4>💡 Upload Tips</h4>
        <ul>
          <li>Ensure documents are clear and readable</li>
          <li>Use good lighting when taking photos</li>
          <li>Avoid shadows and glare</li>
          <li>Make sure all text is visible</li>
          <li>File size should be less than 5MB</li>
          <li>Supported formats: JPG, PNG, PDF</li>
        </ul>
      </div>
    </div>
  );
};

export default MerchantKycDocuments;